import {
  DEFAULT_PAGINATION_PAGE,
  DEFAULT_PAGINATION_PAGE_SIZE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
} from '@shared/constants/pagination.constants';

import { EDashboardSegmentKey, type TDashboardScopeResponse } from '../types/dashboard.types';
import {
  DEFAULT_DASHBOARD_SEGMENT_KEY,
  MAX_DASHBOARD_PAGE_SIZE,
  MAX_DASHBOARD_UI_PAGE,
  DASHBOARD_SEGMENT_PARAM,
  DASHBOARD_ROOT_IDS_PARAM,
} from '../dashboard.constants';

const DASHBOARD_SEGMENT_KEYS = new Set<EDashboardSegmentKey>(Object.values(EDashboardSegmentKey));
const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

export const normalizeDashboardRootIds = (rootIds: Iterable<string>): string[] => [...new Set(rootIds)].sort();

export const clampUiPage = (page: number): number =>
  Math.min(Math.max(page, DEFAULT_PAGINATION_PAGE), MAX_DASHBOARD_UI_PAGE);

export const getValidDashboardRootIds = (selectedRootIds: string[], availableRootIds: Iterable<string>): string[] => {
  const availableRootIdSet = new Set(availableRootIds);
  return normalizeDashboardRootIds(selectedRootIds.filter((rootId) => availableRootIdSet.has(rootId)));
};

export const isDashboardResponseScopeMatch = (
  responseScope: TDashboardScopeResponse | undefined,
  rootIds: string[]
): boolean => responseScope?.selectedRootIds.join(',') === normalizeDashboardRootIds(rootIds).join(',');

type TDashboardApiParams = {
  normalizedRootIds?: string[];
  page?: number;
  pageSize?: number;
};

type TDashboardSegmentQueryIdentityOptions = {
  selectedRootIds: string[];
  segmentKey: EDashboardSegmentKey;
  page: number;
  pageSize: number;
};

export const buildDashboardApiParams = ({
  normalizedRootIds,
  page,
  pageSize,
}: TDashboardApiParams): URLSearchParams => {
  const params = new URLSearchParams();
  if (normalizedRootIds?.length) params.set(DASHBOARD_ROOT_IDS_PARAM, normalizedRootIds.join(','));
  if (page !== undefined) params.set(PAGINATION_PAGE_PARAM, String(page));
  if (pageSize !== undefined) params.set(PAGINATION_PAGE_SIZE_PARAM, String(pageSize));
  return params;
};

export const getDashboardSegmentQueryIdentity = ({
  selectedRootIds,
  segmentKey,
  page,
  pageSize,
}: TDashboardSegmentQueryIdentityOptions): string =>
  JSON.stringify([normalizeDashboardRootIds(selectedRootIds), segmentKey, page, pageSize]);

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  if (!value || !/^\d+$/.test(value)) return fallback;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const getDashboardPaginationFromSearchParams = (
  searchParams: URLSearchParams
): { segmentKey: EDashboardSegmentKey; page: number; pageSize: number } => {
  const requestedSegment = searchParams.get(DASHBOARD_SEGMENT_PARAM) as EDashboardSegmentKey | null;
  const requestedPageSize = parsePositiveInteger(
    searchParams.get(PAGINATION_PAGE_SIZE_PARAM),
    DEFAULT_PAGINATION_PAGE_SIZE
  );

  return {
    segmentKey:
      requestedSegment && DASHBOARD_SEGMENT_KEYS.has(requestedSegment)
        ? requestedSegment
        : DEFAULT_DASHBOARD_SEGMENT_KEY,
    page: clampUiPage(parsePositiveInteger(searchParams.get(PAGINATION_PAGE_PARAM), DEFAULT_PAGINATION_PAGE)),
    pageSize: requestedPageSize <= MAX_DASHBOARD_PAGE_SIZE ? requestedPageSize : DEFAULT_PAGINATION_PAGE_SIZE,
  };
};

export const getDashboardRootIdsFromSearchParams = (searchParams: URLSearchParams): string[] =>
  normalizeDashboardRootIds(
    searchParams
      .getAll(DASHBOARD_ROOT_IDS_PARAM)
      .flatMap((value) => value.split(','))
      .filter((value) => OBJECT_ID_PATTERN.test(value))
  );

export const buildDashboardHref = (selectedRootIds: string[] = []): string => {
  const params = new URLSearchParams();
  normalizeDashboardRootIds(selectedRootIds).forEach((rootId) => params.append(DASHBOARD_ROOT_IDS_PARAM, rootId));
  const query = params.toString();
  return query ? `/dashboard?${query}` : '/dashboard';
};
